import React from "react";

const normalizeStatKey = (value) => {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }

  return raw
    .replace(/^(demon_|goblin_|promo_)/, "projected_")
    .replace(/^(?!projected_)/, "projected_");
};

const getValue = (source, key) => {
  if (!source || !key) {
    return null;
  }

  return Object.prototype.hasOwnProperty.call(source, key) ? source[key] : null;
};

const getSideOddsPayload = (athlete, side, statKey = "") => {
  const marketKey = normalizeStatKey(statKey || athlete?.stat_key);
  const marketOdds =
    getValue(athlete?.ud_odds, marketKey) ||
    getValue(athlete?.odds, marketKey) ||
    null;

  if (marketOdds && typeof marketOdds === "object") {
    const udSide = side === "less" ? "under" : "over";
    const probability =
      marketOdds[`fair_${udSide}_probability`] ??
      marketOdds[`${udSide}_probability`] ??
      null;
    const multiplier = marketOdds[`${udSide}_multiplier`] ?? null;

    if (probability !== null || multiplier !== null) {
      return {
        probability,
        multiplier,
        american: marketOdds[`${udSide}_american`] ?? null,
      };
    }
  }

  return athlete?.ud_odds?.[side] || athlete?.odds?.[side] || null;
};

const getMultiplierFromProbability = (value) => {
  const probability = Number(value);
  if (!Number.isFinite(probability) || probability <= 0 || probability >= 100) {
    return null;
  }

  return (100 / probability) - 1;
};

const getSideOddsMultiplier = (athlete, side, statKey = "") => {
  const sideOdds = getSideOddsPayload(athlete, side, statKey);
  const numericValue = Number(sideOdds?.multiplier);

  if (Number.isFinite(numericValue) && numericValue > 0) {
    return numericValue;
  }

  return getMultiplierFromProbability(sideOdds?.probability);
};

const formatOddsMultiplier = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return "";
  }

  return `${numericValue.toFixed(2)}x`;
};

export const getSideOddsLabel = (athlete, side) => (
  formatOddsMultiplier(getSideOddsMultiplier(athlete, side))
);

export const getSideOddsLabelForStat = (athlete, side, statKey) => (
  formatOddsMultiplier(getSideOddsMultiplier(athlete, side, statKey))
);

export const getHigherPayoutSide = (athlete, statKey = "") => {
  const moreMultiplier = getSideOddsMultiplier(athlete, "more", statKey);
  const lessMultiplier = getSideOddsMultiplier(athlete, "less", statKey);

  if (moreMultiplier === null || lessMultiplier === null || Math.abs(moreMultiplier - lessMultiplier) < 0.00001) {
    return "";
  }

  return moreMultiplier > lessMultiplier ? "more" : "less";
};

export const HigherPayoutIndicator = ({ side = "more", className = "mr-1" }) => (
  <span
    aria-label="Higher payout side"
    className={["athlete-btn__odds-indicator", className].filter(Boolean).join(" ")}
    style={{ display: "inline-flex", height: 14, verticalAlign: "-0.15em", width: 14 }}
    title="Higher payout side"
  >
    <svg aria-hidden="true" fill="none" focusable="false" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.25" viewBox="0 0 20 20" width="14">
      {side === "less" ? (
        <>
          <path d="M5 7l5 5 5-5" />
          <path d="M5 12l5 5 5-5" />
        </>
      ) : (
        <>
          <path d="M5 12l5-5 5 5" />
          <path d="M5 17l5-5 5 5" />
        </>
      )}
    </svg>
  </span>
);
